import { useState, type MouseEvent } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import LanguageSwitcher from "./LanguageSwitcher";
import { MAIN_SITE_URL } from "@/data/site";
import { categories } from "@/data/categories";

const sectionNavItems = [
  { key: "spreadsheet", target: "what-is" },
  { key: "guides", target: "guides" },
  { key: "categories", target: "categories" },
  { key: "faq", target: "faq" },
];

export default function Navbar() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [categoriesOpen, setCategoriesOpen] = useState(false);

  const scrollToSection = (id: string) => {
    setTimeout(() => {
      document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
    }, 80);
  };

  const handleSectionClick = (
    e: MouseEvent<HTMLAnchorElement>,
    target: string
  ) => {
    e.preventDefault();
    setMenuOpen(false);
    if (location.pathname !== "/") {
      navigate("/");
      scrollToSection(target);
    } else {
      scrollToSection(target);
    }
  };

  return (
    <header className="sticky top-0 z-40 border-b border-background-50/10 bg-primary-800">
      <nav className="mx-auto flex max-w-7xl items-center justify-between gap-4 px-4 py-3 md:px-6">
        <Link
          to="/"
          className="flex items-center gap-2 whitespace-nowrap cursor-pointer"
        >
          <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-background-50 text-primary-700">
            <i className="ri-grid-line text-lg"></i>
          </span>
          <span className="font-heading text-lg font-bold text-background-50">
            {t("brand")}
          </span>
        </Link>

        <div className="hidden items-center gap-1 lg:flex">
          <Link
            to="/"
            className="rounded-full px-3 py-2 text-sm font-medium whitespace-nowrap text-background-50/90 hover:bg-primary-700 hover:text-background-50 cursor-pointer transition-colors"
          >
            {t("nav.home")}
          </Link>
          {sectionNavItems.map((item) => (
            <a
              key={item.key}
              href={`#${item.target}`}
              onClick={(e) => handleSectionClick(e, item.target)}
              className="rounded-full px-3 py-2 text-sm font-medium whitespace-nowrap text-background-50/90 hover:bg-primary-700 hover:text-background-50 cursor-pointer transition-colors"
            >
              {t(`nav.${item.key}`)}
            </a>
          ))}
          <Link
            to="/article/how-to-use-basetao-spreadsheet"
            className="rounded-full px-3 py-2 text-sm font-medium whitespace-nowrap text-background-50/90 hover:bg-primary-700 hover:text-background-50 cursor-pointer transition-colors"
          >
            {t("nav.shoppingGuide")}
          </Link>
        </div>

        <div className="flex items-center gap-2">
          <LanguageSwitcher />
          <a
            href={MAIN_SITE_URL}
            target="_blank"
            rel="noopener nofollow"
            className="hidden whitespace-nowrap rounded-full bg-accent-500 px-4 py-2 text-sm font-semibold text-background-50 hover:bg-accent-600 sm:inline-flex cursor-pointer transition-colors"
          >
            {t("nav.visitMain")}
          </a>
          <button
            type="button"
            onClick={() => setMenuOpen((v) => !v)}
            className="flex h-9 w-9 items-center justify-center rounded-lg border border-background-50/20 text-background-50 md:hidden cursor-pointer"
            aria-label="Menu"
          >
            <i className={`${menuOpen ? "ri-close-line" : "ri-menu-line"} text-lg`}></i>
          </button>
        </div>
      </nav>

      {menuOpen && (
        <div className="max-h-[80vh] overflow-y-auto border-t border-background-50/10 bg-primary-800 px-4 py-4 md:hidden">
          <div className="flex flex-col gap-1">
            <Link
              to="/"
              onClick={() => setMenuOpen(false)}
              className="flex items-center gap-2 rounded-lg px-3 py-2.5 text-sm font-medium text-background-50 hover:bg-primary-700 cursor-pointer transition-colors"
            >
              <i className="ri-home-4-line"></i>
              {t("nav.home")}
            </Link>

            {sectionNavItems.map((item) => (
              <a
                key={item.key}
                href={`#${item.target}`}
                onClick={(e) => handleSectionClick(e, item.target)}
                className="rounded-lg px-3 py-2.5 text-sm font-medium text-background-50 hover:bg-primary-700 cursor-pointer transition-colors"
              >
                {t(`nav.${item.key}`)}
              </a>
            ))}

            <Link
              to="/article/how-to-use-basetao-spreadsheet"
              onClick={() => setMenuOpen(false)}
              className="flex items-center gap-2 rounded-lg px-3 py-2.5 text-sm font-medium text-background-50 hover:bg-primary-700 cursor-pointer transition-colors"
            >
              <i className="ri-shopping-bag-3-line"></i>
              {t("nav.shoppingGuide")}
            </Link>

            <button
              type="button"
              onClick={() => setCategoriesOpen((v) => !v)}
              className="mt-2 flex items-center justify-between gap-2 rounded-lg px-3 py-2.5 text-sm font-semibold text-background-50 hover:bg-primary-700 cursor-pointer transition-colors"
            >
              <span className="flex items-center gap-2">
                <i className="ri-apps-2-line"></i>
                {t("categories.title")}
              </span>
              <i
                className={`ri-arrow-down-s-line transition-transform ${
                  categoriesOpen ? "rotate-180" : ""
                }`}
              ></i>
            </button>

            {categoriesOpen && (
              <div className="mt-2 grid grid-cols-2 gap-2 rounded-xl bg-primary-700/40 p-2">
                {categories.map((c) => (
                  <a
                    key={c.key}
                    href={c.url}
                    target="_blank"
                    rel="noopener nofollow"
                    className="flex items-center gap-2 rounded-lg px-3 py-2 text-sm text-background-50/90 hover:bg-primary-700 hover:text-background-50 cursor-pointer transition-colors"
                  >
                    <span className="truncate">{t(`categories.${c.key}`)}</span>
                  </a>
                ))}
              </div>
            )}

            <a
              href={MAIN_SITE_URL}
              target="_blank"
              rel="noopener nofollow"
              className="mt-3 inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full bg-accent-500 px-4 py-2.5 text-center text-sm font-semibold text-background-50 hover:bg-accent-600 cursor-pointer transition-colors"
            >
              {t("nav.visitMain")}
              <i className="ri-arrow-right-up-line"></i>
            </a>
          </div>
        </div>
      )}
    </header>
  );
}